import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useDeepCompareEffect from "use-deep-compare-effect";
import { z } from "zod";

import type { Supplier } from "@/apis/gql/types";
import { AutoSaveForm } from "@/components/shared/form/form-auto-save";
import { IbanInput } from "@/components/shared/form/iban-input";
import {
  ValidationErrorMessage,
  ValidationRoot,
} from "@/components/shared/form/shared/form-validation";
import { Form as FormProvider } from "@/components/ui/form";
import { ibanValidationSchema } from "@/features/invoice-standard-review/validations/payment";
import type { InvoiceDetailsSupplier } from "@/queries/invoices/get";
import { useUpdateInvoice } from "@/queries/invoices/update";
import { zodResolver } from "@hookform/resolvers/zod";

type Props = {
  invoiceId: string;
  iban: string | null;
  supplier: InvoiceDetailsSupplier;
  onValuesChange?: (values: IbanFormValues) => void;
  onFieldFocus: (field: string) => void;
};

export const EditPaymentIbanForm = ({
  invoiceId,
  iban,
  supplier,
  onValuesChange,
  onFieldFocus,
}: Props) => {
  const [updateInvoice] = useUpdateInvoice();

  const handleUpdateIban = (values: IbanFormValues) => {
    updateInvoice({ invoiceId, iban: values.iban });
  };

  return (
    <Form
      values={{
        iban,
      }}
      supplier={supplier}
      onSubmit={handleUpdateIban}
      onValuesChange={onValuesChange}
      onFieldFocus={onFieldFocus}
    />
  );
};

const ibanFormSchema = z.object({
  iban: z.string().optional().nullable(),
});

type IbanFormValues = z.infer<typeof ibanFormSchema>;

type FormProps = {
  values: IbanFormValues;
  index?: number;
  disabled?: boolean;
  supplier: InvoiceDetailsSupplier;
  onSubmit: (values: IbanFormValues) => void;
  onValuesChange?: (values: IbanFormValues) => void;
  onFieldFocus: (field: string) => void;
};

const Form = ({
  values,
  supplier,
  disabled = false,
  onSubmit,
  onValuesChange,
  onFieldFocus,
}: FormProps) => {
  const { t } = useTranslation();

  const form = useForm<IbanFormValues>({
    resolver: zodResolver(ibanFormSchema),
    values,
    resetOptions: {
      keepDirtyValues: true,
    },
  });

  const formValues = form.watch();

  const isDirty = form.formState.isDirty;

  const handleSubmit = (values: IbanFormValues) => {
    onSubmit(values);
  };

  useDeepCompareEffect(() => {
    if (isDirty) {
      onValuesChange?.(formValues);
    }
  }, [formValues, onValuesChange, isDirty]);

  useEffect(() => {
    form.trigger();
  }, [form.trigger]);

  return (
    <ValidationRoot
      schema={ibanValidationSchema}
      values={{
        supplier: {
          paymentDetails: {
            IBAN: supplier?.paymentDetails?.IBAN,
          },
        },
        ...formValues,
      }}
    >
      <FormProvider {...form}>
        <form className="grid grid-cols-2 gap-2">
          <div className="col-span-2">
            <IbanInput
              label={t("payment_details.fields.iban")}
              name="iban"
              disabled={disabled}
              onFocus={() => onFieldFocus("supplier.IBAN")}
            />
            <ValidationErrorMessage name="iban" />
          </div>
          <AutoSaveForm onSubmit={handleSubmit} immediate />
        </form>
      </FormProvider>
    </ValidationRoot>
  );
};

export type EditPaymentIbanFormValues = IbanFormValues;
